import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { FaHeart, FaEye, FaArrowLeft, FaArrowRight, FaRegHeart } from 'react-icons/fa';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { FaRegTrashAlt, FaReply } from 'react-icons/fa';
import { BsSend } from 'react-icons/bs';
import { FaRegCommentDots } from 'react-icons/fa';
import DOMPurify from 'dompurify';
import '../styles/BookReader.css';
import getProfileImageUrl from '../utils/getProfileImageUrl';

const BookReader = () => {
    const { bookId, chapterId } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [currentChapter, setCurrentChapter] = useState(null);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [replyTo, setReplyTo] = useState(null);
    const [showChapters, setShowChapters] = useState(false);
    const [likedChapters, setLikedChapters] = useState({});
    const [views, setViews] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);
    const currentUserId = parseInt(localStorage.getItem('userId'), 10);

    const saveReadingProgress = async () => {
        if (!currentChapter) {
            console.warn('Aktif bölüm bilgisi yok, ilerleme kaydedilemedi.');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const position = { scrollY: window.scrollY, timestamp: Date.now() };

            await api.post(`/bookReader/progress/save`, {
                bookId: parseInt(bookId),
                chapterId: currentChapter.chapter_id,
                position: position,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            console.error('Okuma ilerlemesi kaydedilirken hata oluştu:', error);
        }
    };

    const checkReadingProgress = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const response = await api.get(`/bookReader/progress/${bookId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const { last_chapter_id, position_in_chapter } = response.data;
            
            // ✅ GÜNCELLEME: CockroachDB ID uyumu için String karşılaştırması
            if (String(chapterId) === "1" && last_chapter_id && String(last_chapter_id) !== "1") {
                const confirmContinue = window.confirm('En son kaldığınız yerden devam etmek ister misiniz?');
                if (confirmContinue) {
                    navigate(`/read/${bookId}/${last_chapter_id}`);
                    setTimeout(() => {
                        if (position_in_chapter?.scrollY) {
                            window.scrollTo(0, position_in_chapter.scrollY);
                        }
                    }, 500);
                }
            }
        } catch (error) {
            console.log('Okuma ilerlemesi bulunamadı, ilk bölümden başlıyor.');
        }
    };

    const fetchBookDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            const response = await api.get(`/bookReader/books/${bookId}/${chapterId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = response.data;
            const authorName = data.chapters[0]?.author_name || 'Bilinmeyen Yazar';
            const publishedChapters = data.chapters.filter(chap => chap.is_published);
            
            // ✅ KRİTİK DÜZELTME: "image_c53358.png" içindeki "206" (String) hatasını çözmek için
            const chapter = publishedChapters.find(chap => String(chap.chapter_id) === String(chapterId));

            const initialViews = chapter?.views || 0;

            const updatedLikedChapters = {};
            publishedChapters.forEach(chap => {
                updatedLikedChapters[chap.chapter_id] = chap.is_liked;
            });
            setBook({ ...data, author_name: authorName, chapters: publishedChapters });
            setCurrentChapter(chapter);
            setLikedChapters(updatedLikedChapters);
            setViews(Number.isNaN(initialViews) ? 0 : initialViews);
            await incrementViewCount();
            setLoading(false);
        } catch (error) {
            console.error('Error fetching book details:', error);
            setLoading(false);
        }
    };

    const incrementViewCount = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await api.post(`/bookReader/books/${bookId}/chapters/${chapterId}/incrementViews`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.viewUpdated) {
                setViews(prevViews => prevViews + 1);
            }
        } catch (error) {
            console.error('Error incrementing view count:', error);
        }
    };

    useEffect(() => {
        if (currentChapter) {
            saveReadingProgress();
        }
    }, [chapterId, bookId, currentChapter]);

    useEffect(() => {
        window.scrollTo(0, 0);

        const handleBeforeUnload = () => {
            saveReadingProgress();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        fetchBookDetails();
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [chapterId, bookId]);

    useEffect(() => {
        checkReadingProgress();
    }, [bookId, navigate]);

    useEffect(() => {
        const fetchComments = async () => {
            if (!currentChapter) return;
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    navigate('/login');
                    return;
                }
                const response = await api.get(`/bookReader/chapters/${currentChapter.chapter_id}/comments`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setComments(response.data);
            } catch (error) {
                console.error('Error fetching comments:', error);
            }
        };
        fetchComments();
    }, [currentChapter]);

    const toggleChapters = () => {
        setShowChapters(!showChapters);
    };

    const handleLike = async () => {
        if (!currentChapter) return;
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }
            const response = await api.post(`/bookReader/chapters/${currentChapter.chapter_id}/like`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const isLiked = response.data.isLiked;
            const updatedLikes = isLiked
                ? currentChapter.likes + 1
                : currentChapter.likes - 1;
            setCurrentChapter((prev) => ({
                ...prev,
                likes: updatedLikes,
            }));
            setLikedChapters((prev) => ({
                ...prev,
                [currentChapter.chapter_id]: isLiked,
            }));
        } catch (error) {
            console.error('Error liking the chapter:', error);
        }
    };

    const addComment = async (content, parentId) => {
        if (!content.trim()) return;
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            await api.post(`/bookReader/books/${bookId}/chapters/${currentChapter.chapter_id}/comments`, {
                content: content,
                parentCommentId: parentId,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setNewComment('');
            setReplyTo(null);
            const response = await api.get(`/bookReader/chapters/${currentChapter.chapter_id}/comments`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setComments(response.data);
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const openDeleteModal = (commentId) => {
        setCommentToDelete(commentId);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setCommentToDelete(null);
        setShowDeleteModal(false);
    };

    const handleConfirmDelete = async () => {
        if (!commentToDelete) return;
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            await api.delete(`/bookReader/comments/${commentToDelete}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const removeCommentAndChildren = (id, commentList) => {
                const directReplies = commentList.filter(c => c.parent_comment_id === id);
                let idsToRemove = [id, ...directReplies.flatMap(reply => removeCommentAndChildren(reply.comment_id, commentList))];
                return idsToRemove.flat();
            };
            const allIdsToRemove = removeCommentAndChildren(commentToDelete, comments);
            setComments(prev => prev.filter(c => !allIdsToRemove.includes(c.comment_id)));
            closeDeleteModal();
        } catch (error) {
            console.error('Error deleting comment:', error);
            closeDeleteModal();
        }
    };

    const toggleCommentLike = async (commentId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        try {
            const response = await api.post(`/bookReader/comments/${commentId}/like`, {}, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setComments((prev) => prev.map((comment) => {
                if (comment.comment_id === commentId) {
                    const newLikesCount = response.data.isLiked
                        ? parseInt(comment.likes_count, 10) + 1
                        : Math.max(0, parseInt(comment.likes_count, 10) - 1);
                    return {
                        ...comment,
                        is_liked_by_user: response.data.isLiked,
                        likes_count: newLikesCount,
                    };
                }
                return comment;
            }));
        } catch (error) {
            console.error('Error liking the comment:', error);
        }
    };

    const startReply = (commentId) => {
        setReplyTo(replyTo === commentId ? null : commentId);
    };

    const changeChapter = (newChapterId) => {
        if (newChapterId) {
            navigate(`/read/${bookId}/${newChapterId}`);
        }
    };

    const preventCopying = (event) => {
        if (event.type === 'contextmenu') {
            event.preventDefault();
        }
        if (
            (event.ctrlKey || event.metaKey) &&
            (event.key === 'c' || event.key === 'x' || event.key === 'u' || event.key === 'a')
        ) {
            event.preventDefault();
        }
    };

    useEffect(() => {
        document.addEventListener('contextmenu', preventCopying);
        document.addEventListener('keydown', preventCopying);
        return () => {
            document.removeEventListener('contextmenu', preventCopying);
            document.removeEventListener('keydown', preventCopying);
        };
    }, []);

    const formatContent = (content) => {
        if (!content) return '';
        if (content.includes('<p>') || content.includes('<br>') || content.includes('<div>')) {
            return DOMPurify.sanitize(content);
        }
        const paragraphs = content
            .split(/\n\s*\n/)
            .map(paragraph => paragraph.replace(/\n/g, ' ').trim())
            .filter(paragraph => paragraph.length > 0);
        if (paragraphs.length > 1) {
            return DOMPurify.sanitize(
                paragraphs.map(paragraph => `<p>${paragraph}</p>`).join('')
            );
        } else {
            return DOMPurify.sanitize(
                content
                    .split('\n')
                    .map(line => line.trim())
                    .filter(line => line.length > 0)
                    .map(line => `<p>${line}</p>`)
                    .join('')
            );
        }
    };

    const CommentForm = ({ placeholder, buttonText, onSubmit, isReply = false }) => {
        const [content, setContent] = useState('');

        const handleSubmit = (e) => {
            e.preventDefault();
            if (!content.trim()) return; // Boş yorumu engelle
            onSubmit(content);
            setContent('');
        };

        return (
            <form onSubmit={handleSubmit} className={`comment-form ${isReply ? 'reply-form' : ''}`}>
                <div className="comment-input-wrapper">
                    <input
                        type="text"
                        placeholder={placeholder}
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                    />
                    <button type="submit">{buttonText}</button>
                </div>
            </form>
        );
    };

    const renderComments = (parentId = null) => {
        return comments
            .filter((comment) => comment.parent_comment_id === parentId)
            .map((comment) => (
                <div key={comment.comment_id} className="comment-thread">
                    <div className="comment-card">
                        <img
                            src={getProfileImageUrl(comment.profile_image_url)}
                            alt="Profil"
                            className="comment-avatar"
                        />
                        <div className="comment-main">
                            <div className="comment-header">
                                <span className="comment-username">{comment.username}</span>
                            </div>
                            <p className="comment-text">{comment.content}</p>
                            <div className="comment-footer">
                                <button className="comment-action-button" onClick={() => toggleCommentLike(comment.comment_id)}>
                                    {comment.is_liked_by_user ? 
                                        <FaHeart size={14} color="#310034" /> : 
                                        <FaRegHeart size={14} color="#666" />
                                    }
                                    <span>{comment.likes_count}</span>
                                </button>
                                {parentId === null && (
                                    <button className="comment-action-button" onClick={() => startReply(comment.comment_id)}>
                                        <FaReply size={14} />
                                        <span>Yanıtla</span>
                                    </button>
                                )}
                                {comment.user_id === currentUserId && (
                                    <button className="comment-action-button delete" onClick={() => openDeleteModal(comment.comment_id)}>
                                        <FaRegTrashAlt size={14} />
                                        <span>Sil</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    {replyTo === comment.comment_id && parentId === null && (
                        <CommentForm
                            placeholder={`@${comment.username} adlı kullanıcıya yanıt ver...`}
                            buttonText={<BsSend size={20} />}
                            onSubmit={(content) => addComment(content, comment.comment_id)}
                            isReply={true}
                        />
                    )}
                    <div className="comment-replies">
                        {renderComments(comment.comment_id)}
                    </div>
                </div>
            ));
    };

    if (loading) {
        return (
            <div className="book-reader">
                <div className="loading-spinner">Yükleniyor...</div>
            </div>
        );
    }

    if (!book || !currentChapter) {
        return (
            <div className="book-reader">
                <div className="error-message">Kitap bilgileri yüklenemedi. Lütfen daha sonra tekrar deneyin.</div>
            </div>
        );
    }

    // ✅ GÜNCELLEME: Navigasyon ve index hesabı için String karşılaştırması
    const currentChapterIndex = book.chapters.findIndex(c => String(c.chapter_id) === String(chapterId));
    const nextChapter = book.chapters[currentChapterIndex + 1];
    const prevChapter = book.chapters[currentChapterIndex - 1];

    return (
        <div className="book-reader">
            <header className="header-section">
                <div className="button-strip">
                    <div className="book-header" onClick={toggleChapters}>
                        <div className="book-details">
                            <span className="book-title">{book.title}</span>
                            <span className="book-author">Yazar: {book.author_name}</span>
                        </div>
                        <span className="dropdown-arrow">
                            {showChapters ? <IoIosArrowUp /> : <IoIosArrowDown />}
                        </span>
                    </div>
                    <div className="buttons">
                        <Link to={`/book/${bookId}`}>
                            <button className="back-to-book">
                                <FaArrowLeft /> Kitaba Dön
                            </button>
                        </Link>
                        <button
                            className={`like-button-header ${likedChapters[currentChapter.chapter_id] ? 'liked' : ''}`}
                            onClick={handleLike}
                        >
                            <FaHeart /> {currentChapter.likes}
                        </button>
                    </div>
                </div>
                {showChapters && (
                    <div className="chapter-list">
                        <ul>
                            {book.chapters.map(chap => (
                                <li
                                    key={chap.chapter_id}
                                    className={`chapter-item ${String(chap.chapter_id) === String(chapterId) ? 'active' : ''}`}
                                    onClick={() => {
                                        changeChapter(chap.chapter_id);
                                        setShowChapters(false);
                                    }}
                                >
                                    {chap.title}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </header>

            <main className="read-main">
                <div className="story-info">
                    <h1>{currentChapter.title}</h1>
                    <div className="story-stats">
                        <span>
                            <FaEye /> {views}
                        </span>
                        <span>
                            <FaHeart /> {currentChapter.likes}
                        </span>
                        <span>
                            <FaRegCommentDots /> {comments.length}
                        </span>
                    </div>
                </div>
                <div className="story-content" dangerouslySetInnerHTML={{ __html: formatContent(currentChapter.content) }} />
            </main>

            <div className="chapter-navigation">
                <button
                    onClick={() => changeChapter(prevChapter?.chapter_id)}
                    disabled={!prevChapter}
                >
                    <FaArrowLeft /> Önceki Bölüm
                </button>
                <button
                    onClick={() => changeChapter(nextChapter?.chapter_id)}
                    disabled={!nextChapter}
                >
                    Sonraki Bölüm <FaArrowRight />
                </button>
            </div>

            <section className="comment-section">
                <h3>Yorumlar ({comments.length})</h3>
                <CommentForm
                    placeholder="Yorumunuzu buraya yazın..."
                    buttonText={<BsSend color="white" size={20} />}
                    onSubmit={(content) => addComment(content, null)}
                />
                <div className="comments-list">
                    {renderComments().length > 0 ? renderComments() : <p className="no-comments">Henüz yorum yok.</p>}
                </div>
            </section>

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>Yorumu Silmek İstediğinize Emin misiniz?</h3>
                        <p>Bu işlem geri alınamaz.</p>
                        <div className="modal-actions">
                            <button className="modal-cancel" onClick={closeDeleteModal}>İptal</button>
                            <button className="modal-confirm" onClick={handleConfirmDelete}>Sil</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookReader;